import React, { useState, type FC } from 'react';
import { useAui } from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { refreshThreadHistories } from '@/lib/thread-history-cache';
import {
  updateApprovalDecision,
  useApprovalDecision,
  type ApprovalDecision,
} from '@/lib/approval-decisions';
import { useTranslation } from 'react-i18next';
import {
  approvalFailureCopyKey,
  confirmApproval,
  rejectApproval,
} from '@/lib/agent-threads';
import { resolveActivityCopy } from '../tool-activity-copy';

interface ApprovalBannerProps {
  approvalId: string;
  toolName: string;
  description?: string;
  onApprove?: (approvalId: string) => void;
  onReject?: (approvalId: string, reason?: string) => void;
}

export const ApprovalBanner: FC<ApprovalBannerProps> = ({
  approvalId,
  toolName,
  description,
  onApprove,
  onReject,
}) => {
  const aui = useAui();
  const queryClient = useQueryClient();
  // A decision (or learning it was already made, or expired) changes what a
  // reopened thread offers: no cached history may show this card as pending.
  const refreshHistory = () => refreshThreadHistories(queryClient);
  const { t } = useTranslation('common');
  const [reason, setReason] = useState('');
  // Shared by approval ID: a reopened thread's copy of this card shows the
  // same request running or done (approval-decisions.ts).
  const { loading, resolved, error } = useApprovalDecision(approvalId);
  const decide = (patch: Partial<ApprovalDecision>) => updateApprovalDecision(approvalId, patch);

  const handleApprove = async () => {
    try {
      decide({ error: null, loading: 'approve' });
      onApprove?.(approvalId);
      const response = await confirmApproval(approvalId, reason || undefined);
      if (response.assistant?.text) {
        aui.thread().append({
          role: 'assistant',
          content: [{ type: 'text', text: response.assistant.text }],
          startRun: false,
        });
      }
      decide({ loading: null, resolved: 'approved' });
    } catch (resolveError) {
      // A stale or expired approval (B10) is explained in the user's
      // language; anything else keeps the server's message.
      const copyKey = approvalFailureCopyKey(resolveError);
      decide({
        loading: null,
        error: copyKey
          ? t(copyKey)
          : resolveError instanceof Error
            ? resolveError.message
            : t('chat.approval.approveFailed'),
      });
    } finally {
      refreshHistory();
    }
  };

  const handleReject = async () => {
    try {
      decide({ error: null, loading: 'reject' });
      onReject?.(approvalId, reason || undefined);
      const response = await rejectApproval(approvalId, reason || undefined);
      if (response.assistant?.text) {
        aui.thread().append({
          role: 'assistant',
          content: [{ type: 'text', text: response.assistant.text }],
          startRun: false,
        });
      }
      decide({ loading: null, resolved: 'rejected' });
    } catch (resolveError) {
      decide({
        loading: null,
        error:
          resolveError instanceof Error
            ? resolveError.message
            : t('chat.approval.declineFailed'),
      });
    } finally {
      refreshHistory();
    }
  };

  const label = description || t('chat.approval.confirm');
  const runningLabel = resolveActivityCopy({
    toolName,
    stage: 'running',
    t,
  }).label;

  return (
    <div
      style={{
        padding: '14px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {resolved ? (
        <span
          className="tabbio-menu-badge"
          style={{ width: 'fit-content' }}
        >
          {resolved === 'approved'
            ? t('chat.approval.approved')
            : t('chat.approval.declined')}
        </span>
      ) : null}

      {!resolved ? (
        <>
          <span
            style={{
              fontSize: 13,
              lineHeight: '20px',
              color: 'var(--text-secondary)',
            }}
          >
            {label}
          </span>

          <input
            type="text"
            aria-label={t('chat.approval.reasonOptional')}
            placeholder={t('chat.approval.reasonOptional')}
            value={reason}
            disabled={loading !== null}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && loading === null) void handleReject();
            }}
            style={{
              background: 'rgba(0,0,0,0.04)',
              border: 0,
              color: 'var(--text-primary)',
              fontSize: 13,
              paddingInline: 14,
              height: 36,
              borderRadius: 9999,
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={handleApprove}
              disabled={loading !== null}
              aria-busy={loading === 'approve'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 36,
                paddingInline: 16,
                borderRadius: 9999,
                border: 0,
                background: 'var(--text-primary)',
                color: 'var(--bg)',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: loading ? 'default' : 'pointer',
                opacity: loading === 'reject' ? 0.45 : 1,
                transition: 'opacity 150ms',
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.opacity = '0.88';
              }}
              onMouseLeave={(e) => {
                if (!loading)
                  e.currentTarget.style.opacity = loading === 'reject' ? '0.45' : '1';
              }}
            >
              {loading === 'approve' ? runningLabel : t('chat.approval.approve')}
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={loading !== null}
              aria-busy={loading === 'reject'}
              className="tabbio-chip"
              style={{
                height: 36,
                opacity: loading === 'approve' ? 0.45 : 1,
                cursor: loading ? 'default' : 'pointer',
              }}
            >
              {loading === 'reject'
                ? t('chat.approval.declining')
                : t('chat.approval.decline')}
            </button>
          </div>
        </>
      ) : null}

      {error ? (
        <span
          style={{
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          {error}
        </span>
      ) : null}
    </div>
  );
};
